import { authenticatedIdentity } from "../../_services/apiFetch";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
    FiArrowLeft, FiCalendar, FiCheck, FiCheckCircle, FiChevronDown,
    FiCreditCard, FiDollarSign, FiEdit2, FiEdit3, FiFileText,
    FiHash, FiHome, FiInfo, FiLock, FiMapPin, FiPhone,
    FiPrinter, FiSend, FiTrash2, FiUser, FiX,
} from "react-icons/fi";
import {
    Banner, CancelDocModal, CompanyStatusBanner, DeleteModal, DetailRow, DetailSectionHeading,
    DiscardModal, DocStatusBadge, FieldInput, LineItemsTable,
    SearchableSelect, SectionHeading, SimpleSelect, T, btnStyleDanger,
    btnStyleOutline, btnStylePrimary, round2,
} from "./PurchaseShared";
import { PurchasePrintTemplate } from "./PurchaseShared";
import { SendDocumentDialog, canSendDocumentType } from "../Shared/SendDocumentDialog";
import { listReturnPath } from "../../utils/listReturn";
import { getCurrencySettings } from "../../_services/finance.service";
import { getSettingsRate, localToday, toValidRate, ModuleApprovalActions } from "../Sales/SalesShared";
import {
    FIX_FIELDS_MESSAGE, NO_ITEMS_MESSAGE, positiveMessage, requiredMessage, rowMessage, saveFailedMessage,
} from "../Shared/formErrors";

// Fields this form (and the pages' extra fields) show an error under. A server
// reason for any other field (e.g. piType, a line-item row) goes in the banner.
const SHOWN_ERROR_FIELDS = new Set([
    "date", "deliveryDate", "supplier", "contactPerson", "shippingAddress", "currency", "items",
    "reason", "purchaseOrderId", "refNo", "poRefNo", "linkedPiIds", "payFrom", "paymentMethod",
]);
import {
    getApprovalCapabilities,
    isFinalApprovalDecision,
} from "../../_services/approval.service";
import config from "../../../config";
import "../../Assets/Accounting.css";


// MODULE-LEVEL CONSTANTS
// The user's local date (toISOString() is UTC, which is still yesterday before 8am in Malaysia).
const today = localToday;

const RECORD_TYPE_BY_DOC_TYPE = {
    "Purchase Order": "purchase_order",
    "Direct Purchase Invoice": "purchase_invoice",
    "Indirect Purchase Invoice": "purchase_invoice",
    "Payment Voucher": "payment_voucher",
    "Purchase Credit Note": "purchase_credit_note",
    "Purchase Debit Note": "purchase_debit_note",
};

const NO_APPROVAL_CAPABILITIES = {
    canApprove: false,
    canReject: false,
    currentLevel: null,
    recordIdentity: null,
};

/** Fallback currency list — overwritten by API on mount. */
const DEFAULT_CURRENCIES = [
    { code: "MYR", name: "Malaysian Ringgit", rate: 1.0 },
    { code: "USD", name: "US Dollar", rate: 0.24 },
    { code: "EUR", name: "Euro", rate: 0.20 },
    { code: "GBP", name: "British Pound", rate: 0.17 },
    { code: "SGD", name: "Singapore Dollar", rate: 0.32 },
    { code: "CNY", name: "Chinese Yuan", rate: 1.71 },
    { code: "JPY", name: "Japanese Yen", rate: 26.19 },
    { code: "THB", name: "Thai Baht", rate: 7.86 },
    { code: "IDR", name: "Indonesian Rupiah", rate: 3333.33 },
    { code: "VND", name: "Vietnamese Dong", rate: 5476.19 },
];

/** Reasons shown in the Credit Note / Debit Note reason dropdown. */
const CREDIT_DEBIT_REASONS = [
    "Defective / Damaged",
    "Wrong Item Delivered",
    "Order Cancelled",
    "Price Adjustment",
    "Returned Goods",
    "Overcharged",
];

/** How many Approved Purchase Invoices the CN/DN "PI Reference No." list loads. */
const PI_OPTIONS_PAGE_SIZE = 5000;

/**
 * Items copied from a PI / PO reference, with the currency their prices are in
 * (and that document's rate), so a USD invoice's items aren't saved as MYR.
 */
const itemsWithTheirCurrency = (source) => ({
    items: source.items,
    currency: source.currency || null,
    exchangeRate: source.exchangeRate ?? null,
});

/** The draft with the reference's items — and their currency, when known. */
const applyReferenceItems = (prev, { ref, items, currency, exchangeRate }) => ({
    ...prev,
    purchaseOrderId: ref,
    items,
    ...(currency ? { currency, exchangeRate } : {}),
});

// The default date is added in the EMPTY memo, so it is today's date when the
// form opens, not the date the app was loaded.
const BASE_EMPTY_DRAFT = {
    deliveryDate: "",
    supplier: "",
    supplierAddress: "",
    supplierShippingAddress: "",
    shippingAddress: "",
    contactPerson: "",
    phoneNumber: "",
    poReference: "",
    reason: "",
    paymentTerms: "",
    currency: "MYR",
    status: "Draft",
    notes: "",
    items: [],
};

// PERMISSION HELPER
function getPermissions(status) {
    const canEdit = status === "Draft" || status === "Rejected" || status === "Approved";
    return {
        canEdit,
        canPrint: !["Cancelled", "Void"].includes(status),
        canDelete: status === "Draft" || status === "Rejected",
        viewOnly: !canEdit,
    };
}

function buildSuccessMessage({ isAdd, docType, id, status }) {
    if (status === "Pending for Approval") {
        return isAdd
            ? `${docType} ${id} submitted for approval.`
            : `${docType} submitted for approval.`;
    }
    if (status === "Draft") {
        return isAdd
            ? `${docType} ${id} saved as Draft.`
            : `${docType} saved as Draft.`;
    }
    if (status === "Cancelled") return `${docType} cancelled.`;
    return isAdd
        ? `${docType} ${id} created successfully.`
        : `${docType} updated successfully.`;
}

// PREFILL DRAFT READER  (MRP / CTB flow — reads sessionStorage)
function readPrefillDraft(location) {
    // 1. React Router location state takes priority
    if (location?.state?.purchaseOrderDraft) return location.state.purchaseOrderDraft;

    // 2. Fallback to sessionStorage (MRP / CTB integration)
    try {
        const stored =
            sessionStorage.getItem("mrpPurchaseOrderDraft") ||
            sessionStorage.getItem("ctbPurchaseOrderDraft");
        if (!stored) return null;
        sessionStorage.removeItem("mrpPurchaseOrderDraft");
        sessionStorage.removeItem("ctbPurchaseOrderDraft");
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

// INLINE STYLE HELPERS
const tabBtnStyle = (active) => ({
    padding: "6px 16px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none",
    whiteSpace: "nowrap",
    background: active ? "#fff" : "transparent",
    color: active ? T.accent : T.textMuted,
    boxShadow: active ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
    cursor: "pointer", transition: "all 0.15s",
});

// MAIN COMPONENT
export default function PurchaseDocDetails({
    docType = "Purchase Order",
    basePath = "/purchases/purchase-orders",
    // API
    fetchById,
    updateItem,
    createItem,
    deleteItem,
    // Supplier data loaders
    fetchSupplierNames,
    fetchContactPersons,
    fetchSupplierDetails,
    fetchCompanyProfile,
    // Config
    taxRate = 0,
    searchable = false,
    searchSource = "",
    // Optional reference-list loaders
    fetchPoList = null,
    fetchPiList = null,
    fetchPiById = null,
    // Draft seeding
    emptyDraft = {},
    extraFields = null,
    fillDraft = null,
    // Callbacks
    onDependentFieldChange = null,
    // Presentation
    extraFieldProps = {},
    hideCurrency = false,
    lineItemProps = {},
    // Back navigation override (for Finance approvals)
    backPath = null,
    viewOnly = false,
}) {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const isAdd = id === "add";
    const fromApprovals = searchParams.get("fromApprovals") === "true";
    const recordType = RECORD_TYPE_BY_DOC_TYPE[docType];

    // ── Derived document type flags ──────────────────────────────────────────
    const isPI = docType === "Direct Purchase Invoice" || docType === "Indirect Purchase Invoice";
    const isPV = docType === "Payment Voucher";
    const isPO = docType === "Purchase Order";
    const isPCN = docType === "Purchase Credit Note";
    const isPDN = docType === "Purchase Debit Note";
    const approvalBackPath = backPath || (
        isPO
            ? "/accounting-approvals/purchase-approvals"
            : "/accounting-approvals/finance-approvals"
    );

    // ── Derived empty draft ──────────────────────────────────────────────────
    const EMPTY = useMemo(() => ({ ...BASE_EMPTY_DRAFT, date: today(), ...emptyDraft }), [emptyDraft]);

    // ── Component state ──────────────────────────────────────────────────────
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({ ...EMPTY });
    // Read-only roles stay in view mode even if the URL asks for edit
    const [edit, setEdit] = useState(!viewOnly && (isAdd || searchParams.get("mode") === "edit"));
    const [loading, setLoading] = useState(!isAdd);
    const [saving, setSaving] = useState(false);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const [errors, setErrors] = useState({});
    const [itemFieldErrors, setItemFieldErrors] = useState({});
    const [supplierNames, setSN] = useState([]);
    const [contactPersonOpts, setCPO] = useState([]);
    const [showDiscard, setDiscard] = useState(false);
    const [showCancelConfirm, setCancelConfirm] = useState(false);
    const [showDelConfirm, setDelConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [companyProfile, setCP] = useState(null);
    const [activeTab, setActiveTab] = useState("details");
    const [sendOpen, setSendOpen] = useState(false);
    const [poOptions, setPoOptions] = useState([]);
    const [piOptions, setPiOptions] = useState([]);
    const [cpSelectOpen, setCpSelectOpen] = useState(false);
    const [approvalCapabilities, setApprovalCapabilities] = useState(
        NO_APPROVAL_CAPABILITIES
    );
    const [capabilityRefreshKey, setCapabilityRefreshKey] = useState(0);
    const [refreshingApproval, setRefreshingApproval] = useState(false);
    const [currencySettings, setCurrencySettings] = useState({
        defaultCurrency: "MYR",
        currencies: DEFAULT_CURRENCIES,
    });
    // True once we know the live exchange rates failed to load and we're
    // relying on the hardcoded DEFAULT_CURRENCIES fallback instead — those
    // rates can go stale, so anyone entering prices should be told.
    const [usingFallbackRates, setUsingFallbackRates] = useState(false);

    const prevSupplierRef = useRef("");
    const printRef = useRef(null);
    const bannerTimer = useRef(null);
    // Set synchronously so a fast double-click can't start a second save, and
    // kept on after a create so the buttons stay off until the page leaves.
    const saveInFlightRef = useRef(false);
    const createdRef = useRef(false);
    const recordRequestRef = useRef(0);
    const routeIdRef = useRef(id);
    routeIdRef.current = id;
    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const poPrefillRef = useRef(null);
    const poPrefillAppliedRef = useRef(false);
    const poPrefillEnrichedRef = useRef(false);

    const isInactive = !!(companyProfile && companyProfile.Status !== "Active");
    const currentApprovalIdentity =
        data?.status === "Pending for Approval" &&
            data?.dbId &&
            recordType &&
            String(data?.id) === String(id)
            ? `${recordType}:${id}:${data.dbId}`
            : null;
    const currentApprovalCapabilities =
        approvalCapabilities.recordIdentity === currentApprovalIdentity
            ? approvalCapabilities
            : NO_APPROVAL_CAPABILITIES;

    // ── Print handler ────────────────────────────────────────────────────────
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `${data?.id || docType}-${today()}`,
    });

    // ── Banner with auto-dismiss ─────────────────────────────────────────────
    const showBanner = (type, msg, ms = 4000) => {
        setBanner({ type, msg });
        clearTimeout(bannerTimer.current);
        bannerTimer.current = setTimeout(() => setBanner({ type: "", msg: "" }), ms);
    };

    // ── Effects: one-time loads ──────────────────────────────────────────────

    useEffect(() => {
        if (fetchCompanyProfile) fetchCompanyProfile().then(setCP).catch(() => { });
        if (fetchSupplierNames) fetchSupplierNames().then(setSN).catch(() => { });

        const userStr = localStorage.getItem("user");
        if (!userStr) { setUsingFallbackRates(true); }
        else {
            const user = JSON.parse(userStr);
            getCurrencySettings(user.accountId)
                .then((res) => {
                    if (res?.currencies) {
                        setCurrencySettings(res);
                        setUsingFallbackRates(false);
                        // New documents start in the company's Default Currency (Settings).
                        // Only applied while the draft still holds the untouched fallback
                        // and has no items, so no price conversion is needed.
                        const def = res.defaultCurrency;
                        if (isAdd && !hideCurrency && def && res.currencies.some((c) => c.code === def)) {
                            setDraft((prev) =>
                                (prev.currency || "MYR") === "MYR" && !(prev.items || []).length
                                    ? { ...prev, currency: def }
                                    : prev
                            );
                        }
                    } else {
                        setUsingFallbackRates(true);
                    }
                })
                .catch(() => setUsingFallbackRates(true));
        }
    }, [fetchCompanyProfile, fetchSupplierNames]);

    // ── Effect: load PO options for reference dropdowns ──────────────────────
    useEffect(() => {
        if (!fetchPoList || isPO) return;
        if (!draft.supplier) { setPoOptions([]); return; }
        fetchPoList({ pageSize: 1500 })
            .then((res) => {
                const list = (res?.data || res || []);
                setPoOptions(
                    list.filter((po) => po.status === "Approved" && po.supplier === draft.supplier)
                        .map((po) => po.id)
                        .filter(Boolean)
                );
            })
            .catch(() => { });
    }, [fetchPoList, isPO, draft.supplier]);

    // ── Effect: load PI options for CN/DN reference dropdowns ────────────────
    // Only Approved invoices can be linked (the server checks this too), so
    // ask for those; the supplier is matched ignoring case and spaces.
    useEffect(() => {
        if (!fetchPiList) return;
        if (!draft.supplier) { setPiOptions([]); return; }
        const supplier = draft.supplier.trim().toLowerCase();
        fetchPiList({ pageSize: PI_OPTIONS_PAGE_SIZE, status: "Approved" })
            .then((res) => {
                const list = (res?.data || res || []);
                setPiOptions(
                    list.filter((pi) => (pi.supplier || "").trim().toLowerCase() === supplier)
                        .map((pi) => pi.id)
                        .filter(Boolean)
                );
            })
            .catch(() => { });
    }, [fetchPiList, draft.supplier]);

    // ── Effect: load existing document or seed blank ─────────────────────────
    useEffect(() => {
        const requestGeneration = ++recordRequestRef.current;
        const controller = new AbortController();
        setApprovalCapabilities(NO_APPROVAL_CAPABILITIES);
        setRefreshingApproval(false);
        if (isAdd) {
            setData({ ...EMPTY });
            setLoading(false);
            return () => controller.abort();
        }
        if (!id) return () => controller.abort();
        setLoading(true);
        fetchById(id, { signal: controller.signal })
            .then((d) => {
                if (requestGeneration !== recordRequestRef.current) return;
                setData(d);
                setDraft({ ...EMPTY, ...d });
                prevSupplierRef.current = d?.supplier || "";
                if (!getPermissions(d.status).canEdit) setEdit(false);
            })
            .catch((error) => {
                if (requestGeneration === recordRequestRef.current && error?.name !== "AbortError") {
                    showBanner("danger", `Failed to load ${docType.toLowerCase()}.`);
                }
            })
            .finally(() => {
                if (requestGeneration === recordRequestRef.current) setLoading(false);
            });
        return () => controller.abort();
    }, [id, isAdd]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let cancelled = false;
        setApprovalCapabilities(NO_APPROVAL_CAPABILITIES);
        if (
            data?.status !== "Pending for Approval" ||
            !data?.dbId ||
            !recordType
        ) {
            setApprovalCapabilities(NO_APPROVAL_CAPABILITIES);
            return () => { cancelled = true; };
        }

        const recordIdentity = `${recordType}:${id}:${data.dbId}`;
        getApprovalCapabilities(recordType, data.dbId)
            .then((capabilities) => {
                if (!cancelled) {
                    setApprovalCapabilities({ ...capabilities, recordIdentity });
                }
            })
            .catch((error) => {
                console.error("Failed to load approval capabilities:", error);
                if (!cancelled) setApprovalCapabilities(NO_APPROVAL_CAPABILITIES);
            });

        return () => { cancelled = true; };
    }, [data?.dbId, data?.status, id, recordType, capabilityRefreshKey]);

    // ── Effect: MRP / CTB prefill (runs once on create) ─────────────────────
    useEffect(() => {
        if (!isAdd || poPrefillAppliedRef.current) return;
        const prefill = readPrefillDraft(location);
        if (!prefill) return;

        poPrefillRef.current = prefill;
        poPrefillAppliedRef.current = true;

        setDraft((prev) => ({
            ...prev,
            ...prefill,
            items: Array.isArray(prefill.items) ? prefill.items : prev.items,
        }));
    }, [isAdd, location]);

    // ── Effect: seed shipping address from company profile after prefill ──────
    useEffect(() => {
        if (!isAdd || !poPrefillAppliedRef.current || !companyProfile?.Address) return;
        setDraft((prev) => prev.shippingAddress ? prev : { ...prev, shippingAddress: companyProfile.Address });
    }, [isAdd, companyProfile]);

    // ── Effect: enrich prefill items with inventory prices ───────────────────
    useEffect(() => {
        if (!isAdd || !poPrefillAppliedRef.current || poPrefillEnrichedRef.current) return;

        const prefill = poPrefillRef.current;
        if (!prefill) return;

        let cancelled = false;

        const convertPrice = (item) => {
            const unitPrice = Number(item.unitPrice) || 0;
            if (unitPrice <= 0) return 0;
            const fromRate = item.rate || 1.0;
            const toCurrencyObj = (currencySettings?.currencies || []).find((c) => c.code === (prefill.currency || "MYR"));
            const toRate = toCurrencyObj ? toCurrencyObj.rate : 1.0;
            return round2((unitPrice * toRate) / fromRate);
        };

        const enrichItem = async (item) => {
            if (!item?.itemCode || Number(item.unitPrice) > 0) return item;
            try {
                const url = new URL(`${config.apiUrl}/searchInventoryItems`);
                url.searchParams.append("q", item.itemCode);
                if (searchSource) url.searchParams.append("type", searchSource);
                const res = await fetch(url.toString());
                if (!res.ok) return item;
                const matches = await res.json();
                if (!Array.isArray(matches) || !matches.length) return item;
                const match = matches.find((m) => m.itemCode === item.itemCode) ?? matches[0];
                return {
                    ...item,
                    description: item.description || match.description || "",
                    unit: item.unit || match.unit || "",
                    unitPrice: convertPrice(match),
                };
            } catch (err) {
                console.error("Failed to enrich prefill item:", { itemCode: item.itemCode, err });
                return item;
            }
        };

        const enrichAll = async () => {
            const updates = {};
            const supplier = prefill.supplier || "";
            let contactPerson = prefill.contactPerson || "";

            if (supplier) {
                try {
                    const contacts = fetchContactPersons ? await fetchContactPersons(supplier) : [];
                    contactPerson = contactPerson || contacts?.[0] || "";
                    if (contactPerson) updates.contactPerson = contactPerson;
                } catch (err) {
                    console.error("Failed to fetch contacts for prefill:", { supplier, err });
                }

                try {
                    const details = fetchSupplierDetails ? await fetchSupplierDetails(supplier, contactPerson) : {};
                    updates.supplierAddress = details.supplierAddress || prefill.supplierAddress || "";
                    updates.supplierShippingAddress = details.supplierShippingAddress || prefill.supplierShippingAddress || "";
                    updates.paymentTerms = details.paymentTerms || prefill.paymentTerms || "";
                    updates.phoneNumber = details.phoneNumber || prefill.phoneNumber || "";
                } catch (err) {
                    console.error("Failed to fetch supplier details for prefill:", { supplier, contactPerson, err });
                }
            }

            if (!prefill.shippingAddress && companyProfile?.Address) {
                updates.shippingAddress = companyProfile.Address;
            }

            const items = await Promise.all((prefill.items || []).map(enrichItem));
            if (cancelled) return;

            poPrefillEnrichedRef.current = true;
            setDraft((prev) => ({
                ...prev,
                ...updates,
                items: items.length ? items : prev.items,
            }));
        };

        enrichAll();
        return () => { cancelled = true; };
    }, [isAdd, companyProfile, currencySettings, fetchContactPersons, fetchSupplierDetails, searchSource]);

    // ── Effect: merge fillDraft from parent ──────────────────────────────────
    useEffect(() => {
        if (!fillDraft) return;
        setDraft((prev) => ({ ...prev, ...fillDraft }));
    }, [fillDraft]);

    // ── Effect: load contact persons when supplier changes ───────────────────
    useEffect(() => {
        if (!draft.supplier) {
            setCPO([]);
            prevSupplierRef.current = "";
            return;
        }
        if (fetchContactPersons) {
            fetchContactPersons(draft.supplier).then((p) => setCPO(p || [])).catch(() => setCPO([]));
        }
    }, [draft.supplier, fetchContactPersons]);


    // CHANGE HANDLERS
    /** Generic field change — updates draft and clears field error. */
    const handleDraftChange = (e) => {
        const { name, value } = e.target;
        setDraft((prev) => {
            const next = { ...prev, [name]: value };

            // The stored rate belongs to the old currency; it is re-captured on save.
            if (name === "currency" && value !== prev.currency) next.exchangeRate = null;

            // Convert existing line item prices when the document currency changes
            if (name === "currency" && value && prev.currency && value !== prev.currency && Array.isArray(prev.items) && prev.items.length) {
                const currencies = currencySettings.currencies || [];
                const fromRate = toValidRate(prev.exchangeRate) ?? currencies.find((c) => c.code === prev.currency)?.rate ?? 1.0;
                const toRate = currencies.find((c) => c.code === value)?.rate ?? 1.0;
                next.items = prev.items.map((item) => ({
                    ...item,
                    unitPrice: item.unitPrice != null ? round2((item.unitPrice * toRate) / fromRate) : item.unitPrice,
                }));
            }

            return next;
        });
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    /** Supplier change — resets all downstream fields. */
    const onSupplierChange = (e) => {
        const { value } = e.target;

        setDraft((prev) => ({
            ...prev,
            supplier: value,
            contactPerson: "",
            phoneNumber: "",
            supplierAddress: "",
            paymentTerms: "",
            purchaseOrderId: "",
            poRefNo: "", // Clear PO reference on supplier change
            // Our address is where a Purchase Order ships to; other documents have none.
            shippingAddress: isPO ? companyProfile?.Address || prev.shippingAddress : prev.shippingAddress,
        }));
        setErrors((prev) => ({ ...prev, supplier: "", contactPerson: "" }));

        if (onDependentFieldChange) onDependentFieldChange(value, "");

        if (value && fetchSupplierDetails) {
            fetchSupplierDetails(value, "")
                .then((details) => {
                    setDraft((prev) => ({
                        ...prev,
                        supplierAddress: details.supplierAddress || details.billing_address || "",
                        supplierShippingAddress: details.supplierShippingAddress || details.shipping_address || "",
                        paymentTerms: details.paymentTerms || details.credit_terms || "",
                        contactPerson: "",
                        phoneNumber: "",
                    }));
                })
                .catch(() => {
                    setDraft((prev) => ({
                        ...prev,
                        supplierAddress: "",
                        supplierShippingAddress: "",
                        phoneNumber: "",
                        paymentTerms: "",
                    }));
                });
        }
    };

    /** Contact person change — updates terms & phone. */
    const onContactPersonChange = (e) => {
        const { value } = e.target;
        setDraft((prev) => ({ ...prev, contactPerson: value }));
        setErrors((prev) => ({ ...prev, contactPerson: "" }));

        if (onDependentFieldChange) onDependentFieldChange(draft.supplier, value);

        if (value && fetchSupplierDetails && draft.supplier) {
            fetchSupplierDetails(draft.supplier, value)
                .then((details) => {
                    setDraft((prev) => ({
                        ...prev,
                        supplierAddress: details.supplierAddress || prev.supplierAddress,
                        supplierShippingAddress: details.supplierShippingAddress || prev.supplierShippingAddress,
                        phoneNumber: details.phoneNumber || "",
                        paymentTerms: details.paymentTerms || "",
                    }));
                })
                .catch(() => { });
        } else {
            setDraft((prev) => ({ ...prev, phoneNumber: "", paymentTerms: "" }));
        }
    };

    /**
     * PO / PI reference change.
     * When a PO ref is selected its line items auto-fill the draft;
     * when a PI ref is selected (CN/DN) the PI's items fill the draft.
     */
    const onPoReferenceChange = (e) => {
        const { value } = e.target;
        setDraft((prev) => ({ ...prev, purchaseOrderId: value }));
        setErrors((prev) => ({ ...prev, purchaseOrderId: "" }));
        if (!value) return;

        // The reference's items replace the line items straight away (no question
        // asked) — unless another reference was picked while these were loading.
        const fillItemsFrom = (source) => {
            if (!source?.items?.length) return;
            const picked = { ref: value, ...itemsWithTheirCurrency(source) };
            setDraft((prev) => (prev.purchaseOrderId === value ? applyReferenceItems(prev, picked) : prev));
        };

        // CN / DN: fill from PI
        if (fetchPiById) {
            fetchPiById(value)
                .then(fillItemsFrom)
                .catch(() => { });
            return;
        }

        // PO: fill from PO
        if (!fetchPoList) return;
        fetchPoList({ pageSize: 1500 })
            .then((res) => {
                const list = res?.data || res || [];
                fillItemsFrom(list.find((p) => p.id === value));
            })
            .catch(() => { });
    };


    // A reference picker (PO / PI Reference No.) is unlocked once the supplier and
    // contact person are picked, as in Sales; until then it shows no error.
    const referencePickable = !!draft.supplier && !!draft.contactPerson;
    // A Purchase Credit/Debit Note's Reason is unlocked once its PI is picked.
    const reasonPickable = !(isPCN || isPDN) || !!draft.purchaseOrderId;

    // VALIDATION
    const validate = () => {
        const e = {};
        const rowE = {};
        const items = draft.items || [];

        // Header-level rules
        if (!draft.date) e.date = requiredMessage("Date");
        if (isPO && !draft.deliveryDate) e.deliveryDate = requiredMessage("Delivery Date");
        if (!draft.supplier) e.supplier = requiredMessage("Supplier");
        // Greyed out until a supplier is picked — no error on it until then.
        if (draft.supplier && !draft.contactPerson) e.contactPerson = requiredMessage("Contact person");
        // Only a Purchase Order ships to us; Credit/Debit Notes have no Shipping Address.
        if (isPO && !draft.shippingAddress) e.shippingAddress = requiredMessage("Shipping Address");

        // Payment Voucher
        if (isPV) {
            if (referencePickable && !draft.linkedPiIds?.length) e.linkedPiIds = requiredMessage("PI Reference No.");
            if (!draft.payFrom) e.payFrom = requiredMessage("Pay From");
            if (!draft.paymentMethod) e.paymentMethod = requiredMessage("Payment Method");
        }

        // Invoice-specific validation
        if (docType === "Direct Purchase Invoice") {
            if (!draft.refNo) e.refNo = requiredMessage("Invoice Reference No.");
            if (referencePickable && !draft.poRefNo) e.poRefNo = requiredMessage("PO Reference No.");
        }
        if (docType === "Indirect Purchase Invoice") {
            if (!draft.refNo) e.refNo = requiredMessage("Reference No.");
        }

        // CN / DN reason (only when not using extraFields override)
        if (!isPO && !extraFields && reasonPickable && !draft.reason) e.reason = requiredMessage("Reason");

        // Purchase CN / DN always adjust a Purchase Invoice
        if ((isPCN || isPDN) && referencePickable && !draft.purchaseOrderId) e.purchaseOrderId = requiredMessage("PI Reference No.");

        // Line-item rules
        if (items.length === 0) {
            e.items = NO_ITEMS_MESSAGE;
        } else {
            items.forEach((r, idx) => {
                const re = {};
                const row = (message) => rowMessage(idx, message);

                if (isPV) {
                    const accountCode = (r.accountCode || "").trim();
                    const desc = (r.description || "").trim();
                    const payAmount = Number(r.payAmount);
                    if (!accountCode) re.accountCode = row(requiredMessage("Account Code"));
                    if (!desc) re.description = row(requiredMessage("Description"));
                    if (!Number.isFinite(payAmount) || payAmount <= 0) re.payAmount = row(positiveMessage("Pay Amount"));
                } else {
                    const itemCode = (r.itemCode || "").trim();
                    const desc = (r.description || "").trim();
                    const unit = (r.unit || "").trim();
                    const qty = Number(r.qty);
                    const unitPrice = Number(r.unitPrice);

                    if (!itemCode && !desc) re.itemCode = row(requiredMessage("Item Code"));
                    if (!desc) re.description = row(requiredMessage("Description"));
                    if (!unit) re.unit = row(requiredMessage("Unit"));
                    if (!Number.isFinite(qty) || qty <= 0) re.qty = row(positiveMessage("Quantity"));
                    if (!Number.isFinite(unitPrice) || unitPrice <= 0) re.unitPrice = row(positiveMessage("Unit Price"));
                }

                if (Object.keys(re).length > 0) rowE[idx] = re;
            });
        }

        setErrors(e);
        setItemFieldErrors(rowE);
        return Object.keys(e).length === 0 && Object.keys(rowE).length === 0;
    };

    // SAVE
    const save = async (targetStatus) => {
        if (saveInFlightRef.current || createdRef.current) return;
        if (isInactive) return showBanner("danger", "Company status is Inactive. Action restricted.");
        if (!validate()) return showBanner("danger", FIX_FIELDS_MESSAGE);

        saveInFlightRef.current = true;
        setSaving(true);
        try {
            const items = draft.items || [];

            // Grand total calculation
            let grandTotal;
            if (isPV) {
                grandTotal = items.reduce((s, r) => s + (Number(r.payAmount) || 0), 0)
                    || Number(draft.payAmount) || 0;
            } else {
                const subtotal = items.reduce((s, r) => s + round2((Number(r.qty) || 0) * (Number(r.unitPrice) || 0)), 0);
                const taxAmt = round2(subtotal * (taxRate / 100));
                grandTotal = round2(subtotal + taxAmt);
            }

            if (isPCN) grandTotal = -Math.abs(grandTotal);

            const finalDraft = { ...draft, status: targetStatus || draft.status, grandTotal };
            // Rate captured with the document so later Settings changes do not re-rate it.
            finalDraft.exchangeRate = toValidRate(finalDraft.exchangeRate) ?? (usingFallbackRates ? null : getSettingsRate(currencySettings, finalDraft.currency || "MYR"));
            const res = isAdd ? await createItem(finalDraft) : await updateItem(id, finalDraft);
            const returnedRecord = res && typeof res === "object" ? res : {};
            const persistedRecord = {
                ...finalDraft,
                ...returnedRecord,
                status: returnedRecord.status || finalDraft.status,
            };
            const savedStatus = persistedRecord.status;
            if (savedStatus === "Pending for Approval" || (!isAdd && data?.status === "Pending for Approval")) {
                window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
            }

            if (isAdd) {
                const newId = res?.id || res?.pv_id || res?.po_id || res?.pcn_id || res?.pdn_id;
                if (!newId) throw new Error("No ID returned from server.");
                createdRef.current = true;
                showBanner("success", buildSuccessMessage({
                    isAdd: true,
                    docType,
                    id: newId,
                    status: savedStatus,
                }));
                setTimeout(() => navigate(`${basePath}?page=1`, { replace: true }), 1200);
            } else {
                showBanner("success", buildSuccessMessage({
                    isAdd: false,
                    docType,
                    id,
                    status: savedStatus,
                }));
                setData(persistedRecord);
                setDraft({ ...EMPTY, ...persistedRecord });
                setEdit(false);
            }
        } catch (err) {
            // Highlight the fields the server refused (e.g. deliveryDate, purchaseOrderId).
            if (err?.fields) setErrors((prev) => ({ ...prev, ...err.fields }));
            showBanner("danger", saveFailedMessage(docType, err, (key) => SHOWN_ERROR_FIELDS.has(key)));
        } finally {
            saveInFlightRef.current = false;
            // After a create, keep the buttons disabled until the page navigates away.
            if (!createdRef.current) setSaving(false);
        }
    };

    // CANCEL an Approved document — sends only the status change, so an older
    // document that predates today's form checks can still be cancelled.
    const handleCancelDocument = async () => {
        if (saveInFlightRef.current) return;
        if (isInactive) {
            showBanner("danger", "Action restricted. Your company status is currently Inactive.");
            return;
        }
        saveInFlightRef.current = true;
        setSaving(true);
        try {
            const res = await updateItem(id, { status: "Cancelled" });
            const returnedRecord = res && typeof res === "object" ? res : {};
            const updatedData = { ...data, ...returnedRecord, status: returnedRecord.status || "Cancelled" };
            setData(updatedData);
            setDraft({ ...EMPTY, ...updatedData });
            showBanner("success", buildSuccessMessage({ isAdd: false, docType, id, status: updatedData.status }));
        } catch (err) {
            showBanner("danger", `Failed to cancel ${docType.toLowerCase()}. ${err?.message ? `(${err.message})` : ""}`.trim());
        } finally {
            saveInFlightRef.current = false;
            setSaving(false);
            setCancelConfirm(false);
        }
    };

    // DELETE a Draft or Rejected document, then go back to the list.
    const handleDelete = async () => {
        if (isInactive) {
            showBanner("danger", "Action restricted. Your company status is currently Inactive.");
            return;
        }
        setDeleting(true);
        try {
            if (deleteItem) await deleteItem(id);
            navigate(listReturnPath(basePath), { replace: true });
        } catch (err) {
            showBanner("danger", `Failed to delete ${docType.toLowerCase()}. ${err?.message ? `(${err.message})` : ""}`.trim());
        } finally {
            setDeleting(false);
            setDelConfirm(false);
        }
    };

    // SEND — emails the document (PDF of its print layout) to the supplier.
    const handleSend = () => {
        if (isInactive) return showBanner("danger", "Action restricted. Company is Inactive.");
        setSendOpen(true);
    };

    const handleDecision = async (result, { action, reason }) => {
        const decisionGeneration = recordRequestRef.current;
        const decisionId = id;
        const decisionIdentity = authenticatedIdentity();
        const isCurrentRecord = () =>
            mountedRef.current &&
            decisionGeneration === recordRequestRef.current &&
            String(routeIdRef.current) === String(decisionId) &&
            decisionIdentity === authenticatedIdentity();

        setRefreshingApproval(true);
        setApprovalCapabilities(NO_APPROVAL_CAPABILITIES);
        const finalApproval = action === "approve" && isFinalApprovalDecision(result);
        const status = action === "reject"
            ? "Rejected"
            : finalApproval
                ? "Approved"
                : "Pending for Approval";
        const changes = action === "reject"
            ? { status, rejectionReason: reason }
            : { status };
        setData((current) => current ? { ...current, ...changes } : current);
        setDraft((current) => ({ ...current, ...changes }));
        if (action === "approve" && !finalApproval) {
            setCapabilityRefreshKey((current) => current + 1);
        }

        showBanner(
            "success",
            action === "reject"
                ? `${docType} rejected.`
                : finalApproval
                    ? `${docType} approved.`
                    : `Approval recorded. ${docType} is pending the next approval level.`
        );
        let refreshSucceeded = true;
        try {
            const refreshed = await fetchById(decisionId);
            if (isCurrentRecord() && refreshed) {
                setData(refreshed);
                setDraft((current) => ({ ...current, ...refreshed }));
            }
        } catch {
            refreshSucceeded = false;
            if (isCurrentRecord()) {
                showBanner("info", "Decision saved successfully, but latest details could not be refreshed. Reload the page.");
            }
        }

        if (isCurrentRecord()) {
            setRefreshingApproval(false);
            if (fromApprovals && refreshSucceeded) navigate(listReturnPath(approvalBackPath));
        }
    };

    const isDirty = () => {
        const base = isAdd ? EMPTY : data;
        if (!base) return false;
        const keys = [
            "date", "deliveryDate", "shippingAddress", "poReference", "reason", "supplier", "supplierAddress",
            "contactPerson", "phoneNumber", "paymentTerms", "notes", "items",
        ];
        // A new form switches to the company's default currency by itself, so
        // only a changed currency on an existing document counts.
        if (!isAdd) keys.push("currency");
        if (JSON.stringify(draft.items ?? []) !== JSON.stringify(base.items ?? [])) return true;
        return keys.some((k) => k !== "items" && JSON.stringify(draft[k] ?? "") !== JSON.stringify(base[k] ?? ""));
    };

    // CANCEL EDIT
    const cancelEdit = () => {
        if (isDirty()) { setDiscard(true); return; }
        if (isAdd) { navigate(listReturnPath(basePath)); return; }
        setDraft({ ...EMPTY, ...data });
        prevSupplierRef.current = data?.supplier || "";
        setEdit(false);
        setErrors({});
    };

    // Read-only roles cannot reach the add form by typing the /add URL directly
    if (viewOnly && isAdd) return <Navigate to={basePath} replace />;

    if (loading)
        return <div style={{ padding: 40, color: T.textMuted, fontSize: 14 }}>Loading {docType.toLowerCase()}…</div>;

    if (!isAdd && !loading && !data)
        return (
            <div style={{ padding: 40 }}>
                <Banner type="danger" msg={`${docType} "${id}" was not found.`} onClose={() => { }} />
            </div>
        );

    // DERIVED DISPLAY VALUES
    const perms = getPermissions(data?.status || "Draft");

    // Set by the server when an Approved document is used by pending/approved
    // ones — it can no longer be edited or cancelled: a Purchase Order used by
    // Purchase Invoices, or a Purchase Invoice used by Payment Vouchers or
    // Purchase Credit/Debit Notes.
    const lockedReason = data?.status === "Approved" ? data?.lockedReason || null : null;

    const totalLabel = isPCN
        ? "Credit Amount"
        : docType === "Purchase Debit Note"
            ? "Debit Amount"
            : "Grand Total";

    const showTabs = true;

    // RENDER
    return (
        <div style={{ padding: "20px 0", width: "100%", boxSizing: "border-box" }}>
            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />
            <CompanyStatusBanner isInactive={isInactive} onNavigate={() => navigate("/contacts/company")} />
            {/* Live exchange rates failed to load — prices/conversions below are
                using built-in fallback rates that may be out of date. Only worth
                surfacing while the user is actually entering prices. */}
            {!hideCurrency && edit && usingFallbackRates && (
                <Banner
                    type="info"
                    msg="Couldn't load live exchange rates — using saved fallback rates, which may be outdated. Double-check converted prices before saving."
                    onClose={() => setUsingFallbackRates(false)}
                />
            )}

            {/* ── Page header bar ────────────────────────────────────────── */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "nowrap" }}>
                {/* Back button */}
                {!isAdd && (
                    <button
                        onClick={() => {
                            if (edit) return;
                            if (fromApprovals) navigate(listReturnPath(approvalBackPath));
                            else navigate(listReturnPath(basePath));
                        }}
                        disabled={edit}
                        title={edit ? "Save or cancel changes before going back" : `Back to ${docType} list`}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 36, padding: "0 14px", boxSizing: "border-box", borderRadius: T.radiusSm, border: `1.5px solid ${T.border}`, background: edit ? T.bg : "#fff", color: edit ? T.textLight : T.textMuted, fontSize: 13, fontWeight: 500, cursor: edit ? "not-allowed" : "pointer", opacity: edit ? 0.5 : 1, transition: "all 0.15s" }}
                    >
                        <FiArrowLeft size={14} /> {fromApprovals ? "Back to Approvals" : "Back"}
                    </button>
                )}

                <h4 style={{ margin: 0, fontWeight: 700, color: T.textPrimary, fontSize: 18, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {isAdd ? `Create ${docType}` : `${docType} Details`}
                </h4>

                {/* Tab switcher (hidden for Purchase Invoices — no print template) */}
                {showTabs && (
                    <div style={{ display: "flex", gap: 4, background: T.bg, padding: 3, borderRadius: T.radiusSm, marginLeft: 4, flexShrink: 0 }}>
                        <button onClick={() => setActiveTab("details")} style={tabBtnStyle(activeTab === "details")}>Details</button>
                        <button onClick={() => setActiveTab("preview")} style={tabBtnStyle(activeTab === "preview")}>Preview {docType}</button>
                    </div>
                )}

                {/* View-mode action buttons */}
                {!isAdd && !edit && perms.canPrint && (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                        {fromApprovals && data?.status === "Pending for Approval" &&
                            (currentApprovalCapabilities.canApprove || currentApprovalCapabilities.canReject) && (
                                <ModuleApprovalActions
                                    docType={docType}
                                    key={`${recordType}:${id}:${data.dbId}`}
                                    recordType={recordType}
                                    recordId={data.dbId}
                                    recordLabel={data.id}
                                    currentLevel={currentApprovalCapabilities.currentLevel}
                                    canApprove={currentApprovalCapabilities.canApprove}
                                    canReject={currentApprovalCapabilities.canReject}
                                    disabled={refreshingApproval}
                                    onDecision={handleDecision}
                                    onError={(error, { phase }) => showBanner(phase === "refresh" ? "info" : "danger", error.message)}
                                />
                            )}
                        {data?.status === "Approved" && !isPI && canSendDocumentType(docType) && (
                            <button
                                onClick={handleSend}
                                disabled={isInactive}
                                style={{ ...btnStyleOutline, opacity: isInactive ? 0.6 : 1 }}
                                title="Email this document to the supplier"
                            >
                                <FiSend size={13} /> Send
                            </button>
                        )}
                        {showTabs && (
                            <button onClick={handlePrint} style={btnStyleOutline}>
                                <FiPrinter size={13} /> Print / Save PDF
                            </button>
                        )}
                        {data?.status === "Approved" && !viewOnly && !lockedReason && (
                            <button
                                onClick={() => setCancelConfirm(true)}
                                disabled={isInactive || saving}
                                style={{ ...btnStyleDanger, opacity: isInactive || saving ? 0.7 : 1, cursor: isInactive || saving ? "not-allowed" : "pointer" }}
                            >
                                <FiX size={13} /> Cancel
                            </button>
                        )}
                        {perms.canEdit && !viewOnly && !lockedReason && (
                            <button onClick={() => setEdit(true)} style={btnStylePrimary}>
                                <FiEdit3 size={13} /> Edit
                            </button>
                        )}
                        {perms.canDelete && !viewOnly && deleteItem && (
                            <button
                                onClick={() => setDelConfirm(true)}
                                disabled={isInactive}
                                style={{ ...btnStyleDanger, background: isInactive ? "#94a3b8" : T.danger, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                            >
                                <FiTrash2 size={13} /> Delete
                            </button>
                        )}
                    </div>
                )}

                {/* Edit-mode save buttons (existing doc) */}
                {edit && !isAdd && (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                        <button onClick={cancelEdit} disabled={saving} style={btnStyleOutline}><FiX size={13} /> Cancel</button>
                        {/* Editing any existing doc — including a previously Approved one — must
                            go back through Draft/Submit rather than silently staying Approved. */}
                        <button
                            onClick={() => save("Draft")}
                            disabled={saving || isInactive}
                            style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#93c5fd" : T.accent, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                        >
                            <FiCheck size={13} /> Save as Draft
                        </button>
                        <button
                            onClick={() => save("Pending for Approval")}
                            disabled={saving || isInactive}
                            style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#a7f3d0" : T.success, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                        >
                            <FiCheck size={13} /> Submit for Approval
                        </button>
                    </div>
                )}
            </div>

            {/* Why Edit / Cancel are hidden (Approved document used by others — see lockedReason) */}
            {lockedReason && !edit && (
                <div role="note" style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 16, padding: "10px 14px", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: T.radiusSm, fontSize: 13, color: "#3730a3" }}>
                    <FiLock size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{lockedReason}</span>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                PREVIEW TAB
            ════════════════════════════════════════════════════════════════ */}
            {activeTab === "preview" && showTabs && (
                <div style={{ background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, padding: "24px", overflow: "auto" }}>
                    {isAdd && (
                        <div style={{ marginBottom: 12, padding: "8px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: T.radiusSm, fontSize: 13, color: "#92400e", fontWeight: 500 }}>
                            Preview mode — fill in the form to see a live preview.
                        </div>
                    )}
                    <PurchasePrintTemplate
                        ref={printRef}
                        data={edit || isAdd ? draft : (data || draft)}
                        companyProfile={companyProfile}
                        docType={docType}
                        taxRate={taxRate}
                        forceNegativeTotals={isPCN}
                    />
                </div>
            )}

            {sendOpen && data && (
                <SendDocumentDialog
                    docs={[data]}
                    docType={docType}
                    party="supplier"
                    Template={PurchasePrintTemplate}
                    companyProfile={companyProfile}
                    forceNegativeTotals={isPCN}
                    onClose={() => setSendOpen(false)}
                    onDone={({ type, msg }) => showBanner(type, msg, type === "danger" ? 10000 : 6000)}
                />
            )}

            {/* ═══════════════════════════════════════════════════════════════
                DETAILS TAB
            ════════════════════════════════════════════════════════════════ */}
            {activeTab === "details" && (
                <>
                    {/* Hidden print target (always mounted so useReactToPrint can find it) */}
                    <div style={{ display: "none" }}>
                        <PurchasePrintTemplate
                            ref={printRef}
                            data={data || draft}
                            companyProfile={companyProfile}
                            docType={docType}
                            taxRate={taxRate}
                            forceNegativeTotals={isPCN}
                        />
                    </div>

                    {/* Document profile strip (view mode) */}
                    {!isAdd && data && !edit && (
                        <div style={{ background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, padding: "22px 28px", marginBottom: 16 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
                                {docType.toUpperCase()} PROFILE
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
                                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#eff6ff", color: T.accent, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700 }}>
                                    {(data.supplier || docType).split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: T.textPrimary }}>{data.supplier}</div>
                                    <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>{data.contactPerson}</div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
                                        <span style={{ fontSize: 13, color: T.textMuted }}>ID: <strong style={{ color: T.textPrimary }}>{data.id}</strong></span>
                                        {data.date && <span style={{ fontSize: 13, color: T.textMuted }}>Date: <strong style={{ color: T.textPrimary }}>{data.date}</strong></span>}
                                        {data.deliveryDate && <span style={{ fontSize: 13, color: T.textMuted }}>Delivery: <strong style={{ color: T.textPrimary }}>{data.deliveryDate}</strong></span>}
                                        <DocStatusBadge value={data.status} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Main form / view card ─────────────────────────────────── */}
                    <div style={{ background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: "visible", marginBottom: 16 }}>

                        {/* ── EDIT MODE ─────────────────────────────────────────── */}
                        {edit ? (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", borderBottom: `1px solid ${T.border}` }}>

                                {/* Column 1 — Supplier Info */}
                                <div style={{ padding: "22px 24px", borderRight: `1px solid ${T.border}` }}>
                                    <SectionHeading icon={FiUser} title="Supplier Info" />

                                    <SearchableSelect
                                        label="Company Name"
                                        name="supplier"
                                        value={draft.supplier}
                                        onChange={onSupplierChange}
                                        options={supplierNames}
                                        placeholder="Type or select supplier..."
                                        required
                                        error={errors.supplier}
                                    />

                                    {/* Contact Person */}
                                    <div style={{ marginBottom: 14 }}>
                                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                                            Contact Person{contactPersonOpts.length > 0 && <span style={{ color: T.danger }}> *</span>}
                                        </label>
                                        {!draft.supplier ? (
                                            <input type="text" disabled placeholder="Select supplier first..." style={{ width: "100%", height: 36, padding: "0 11px", fontSize: 13, border: `1.5px solid ${T.border}`, borderRadius: T.radiusSm, background: T.bg, boxSizing: "border-box", display: "block" }} />
                                        ) : contactPersonOpts.length > 0 ? (
                                            <div style={{ position: "relative" }}>
                                                <select
                                                    name="contactPerson"
                                                    value={draft.contactPerson}
                                                    onChange={onContactPersonChange}
                                                    onFocus={() => setCpSelectOpen(true)}
                                                    onBlur={() => setCpSelectOpen(false)}
                                                    style={{ width: "100%", height: 36, padding: "0 28px 0 11px", fontSize: 13, color: T.textPrimary, border: `1.5px solid ${errors.contactPerson ? T.danger : T.border}`, borderRadius: T.radiusSm, outline: "none", appearance: "none", WebkitAppearance: "none", background: "#fff", boxSizing: "border-box", cursor: "pointer" }}
                                                >
                                                    <option value="">Select Contact Person...</option>
                                                    {contactPersonOpts.map((n) => <option key={n}>{n}</option>)}
                                                </select>
                                                <span style={{ position: "absolute", right: 9, top: "50%", transform: `translateY(-50%) rotate(${cpSelectOpen ? 180 : 0}deg)`, pointerEvents: "none", transition: "transform 0.2s ease", display: "inline-flex", alignItems: "center", color: T.textLight }}>
                                                    <FiChevronDown size={14} />
                                                </span>
                                            </div>
                                        ) : (
                                            <input type="text" name="contactPerson" value={draft.contactPerson} onChange={onContactPersonChange} placeholder="Type contact person name..." style={{ width: "100%", height: 36, padding: "0 11px", fontSize: 13, color: T.textPrimary, border: `1.5px solid ${errors.contactPerson ? T.danger : T.border}`, borderRadius: T.radiusSm, outline: "none", background: "#fff", boxSizing: "border-box", display: "block" }} />
                                        )}
                                        {errors.contactPerson && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{errors.contactPerson}</div>}
                                    </div>

                                    <FieldInput label="Phone Number" name="phoneNumber" value={draft.phoneNumber} readOnly placeholder="Auto-filled" />
                                    <FieldInput label="Supplier Billing Address" name="supplierAddress" value={draft.supplierAddress} as="textarea" rows={3} readOnly placeholder="Auto-filled" />
                                    {!isPV && (
                                        <FieldInput label="Supplier Shipping Address" name="supplierShippingAddress" value={draft.supplierShippingAddress} as="textarea" rows={3} readOnly placeholder="Auto-filled" />
                                    )}
                                </div>

                                {/* Column 2 — Document Info */}
                                <div style={{ padding: "22px 24px", borderRight: `1px solid ${T.border}` }}>
                                    <SectionHeading icon={FiCalendar} title="Document Info" />

                                    <FieldInput label="Date" name="date" type="date" value={draft.date} onChange={handleDraftChange} required error={errors.date} />

                                    {isPO && (
                                        <FieldInput label="Delivery Date" name="deliveryDate" type="date" value={draft.deliveryDate} onChange={handleDraftChange} required error={errors.deliveryDate} />
                                    )}

                                    {isPO && (
                                        <FieldInput label="Shipping Address" name="shippingAddress" value={draft.shippingAddress} onChange={handleDraftChange} as="textarea" rows={3} required error={errors.shippingAddress} />
                                    )}

                                    {/* PO Reference — shown on CN/DN without extraFields */}
                                    {!isPO && !extraFields && !fetchPiList && (
                                        <SearchableSelect
                                            label="PO Reference No."
                                            name="purchaseOrderId"
                                            value={draft.purchaseOrderId || ""}
                                            options={poOptions}
                                            onChange={onPoReferenceChange}
                                            placeholder="Select PO reference..."
                                            disabled={!referencePickable}
                                        />
                                    )}

                                    {/* PI Reference — shown when fetchPiList is provided */}
                                    {/* A dropdown like the Invoice Ref. on Sales Credit/Debit Notes. */}
                                    {fetchPiList && (
                                        <SimpleSelect
                                            label="PI Reference No."
                                            name="purchaseOrderId"
                                            value={draft.purchaseOrderId || ""}
                                            // Editing a note whose invoice is no longer listed still shows it.
                                            options={draft.purchaseOrderId && !piOptions.includes(draft.purchaseOrderId)
                                                ? [draft.purchaseOrderId, ...piOptions]
                                                : piOptions}
                                            onChange={onPoReferenceChange}
                                            placeholder="Select PI (Approved)..."
                                            disabled={!referencePickable}
                                            required={isPCN || isPDN}
                                            error={errors.purchaseOrderId}
                                        />
                                    )}

                                    {/* Reason — CN/DN without extraFields */}
                                    {!isPO && !extraFields && (
                                        <SearchableSelect
                                            label={isPDN ? "Reason for Debit" : "Reason for Credit"}
                                            name="reason"
                                            value={draft.reason || ""}
                                            options={CREDIT_DEBIT_REASONS}
                                            onChange={handleDraftChange}
                                            allowFreeText
                                            required
                                            // Unlocked once the PI is picked, as on Sales Credit/Debit Notes.
                                            disabled={!reasonPickable}
                                            error={errors.reason}
                                            placeholder="Type or select a reason..."
                                        />
                                    )}

                                    {/* Doc-specific extra fields slot */}
                                    {extraFields?.left?.({ draft, onChange: handleDraftChange, errors, edit, ...extraFieldProps })}
                                </div>

                                {/* Column 3 — Terms & Status */}
                                <div style={{ padding: "22px 24px" }}>
                                    <SectionHeading icon={FiDollarSign} title="Terms & Status" />

                                    <FieldInput label="Payment Terms" name="paymentTerms" value={draft.paymentTerms} readOnly placeholder="Auto-filled" />

                                    {!hideCurrency && (
                                        <SimpleSelect
                                            label="Currency"
                                            name="currency"
                                            value={draft.currency || "MYR"}
                                            options={currencySettings.currencies.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                                            onChange={handleDraftChange}
                                            error={errors.currency}
                                        />
                                    )}

                                    <div style={{ marginBottom: 14 }}>
                                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>Status</label>
                                        <div style={{ height: 36, display: "flex", alignItems: "center" }}>
                                            <DocStatusBadge value={draft.status} />
                                        </div>
                                    </div>

                                    <FieldInput label="Notes / Remarks" name="notes" as="textarea" rows={3} value={draft.notes || ""} onChange={handleDraftChange} placeholder="Any additional notes…" />
                                </div>
                            </div>

                        ) : (
                            /* ── VIEW MODE ──────────────────────────────────── */
                            data && (
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                                    {/* Left — Document info */}
                                    <div style={{ padding: "24px 28px", borderRight: `1px solid ${T.border}` }}>
                                        <DetailSectionHeading icon={FiFileText} title="Document Information" />
                                        <DetailRow icon={FiHash} label="Document No.">{data.id}</DetailRow>
                                        <DetailRow icon={FiCalendar} label="Date">{data.date}</DetailRow>
                                        {isPO && <DetailRow icon={FiCalendar} label="Delivery Date">{data.deliveryDate || "—"}</DetailRow>}
                                        {isPO && <DetailRow icon={FiCreditCard} label="Payment Terms">{data.paymentTerms || "—"}</DetailRow>}
                                        {isPO && data.shippingAddress && (
                                            <DetailRow icon={FiMapPin} label="Shipping Address">{data.shippingAddress}</DetailRow>
                                        )}
                                        {!isPO && !extraFields && data.purchaseOrderId && (
                                            <DetailRow icon={FiHash} label="PO Reference">{data.purchaseOrderId}</DetailRow>
                                        )}
                                        {!isPO && !extraFields && data.reason && (
                                            <DetailRow icon={FiEdit2} label="Reason">{data.reason}</DetailRow>
                                        )}
                                        {extraFields?.left?.({ draft: data, onChange: () => { }, errors: {}, edit: false, ...extraFieldProps })}
                                        {data.notes && (
                                            <DetailRow icon={FiEdit2} label="Notes / Remarks">
                                                <div style={{ marginTop: 2 }}>
                                                    {data.notes.split("\n").map((line, i) => (
                                                        <p key={i} style={{ margin: "0 0 8px 0", minHeight: line ? 0 : "1em", color: T.textPrimary }}>{line}</p>
                                                    ))}
                                                </div>
                                            </DetailRow>
                                        )}
                                    </div>

                                    {/* Right — Supplier & Status */}
                                    <div style={{ padding: "24px 28px" }}>
                                        <DetailSectionHeading icon={FiUser} title="Supplier & Status" />
                                        <DetailRow icon={FiHome} label="Company Name">{data.supplier}</DetailRow>
                                        <DetailRow icon={FiUser} label="Contact Person">{data.contactPerson || "—"}</DetailRow>
                                        <DetailRow icon={FiPhone} label="Phone Number">{data.phoneNumber || "—"}</DetailRow>
                                        <DetailRow icon={FiMapPin} label="Supplier Billing Address">{data.supplierAddress || "—"}</DetailRow>
                                        {!isPV && data.supplierShippingAddress && (
                                            <DetailRow icon={FiMapPin} label="Supplier Shipping Address">{data.supplierShippingAddress}</DetailRow>
                                        )}
                                        <DetailRow icon={FiCheckCircle} label="Status">
                                            <DocStatusBadge value={data.status} />
                                        </DetailRow>
                                        {data.createdAt && (
                                            <div style={{ marginTop: 16, padding: "12px 14px", background: T.bg, borderRadius: T.radiusSm, fontSize: 12, color: T.textMuted }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5, fontWeight: 700, color: T.textPrimary }}>
                                                    <FiInfo size={11} /> Metadata
                                                </div>
                                                <div>Created: {new Date(data.createdAt).toLocaleString()}</div>
                                                {data.updatedAt && <div style={{ marginTop: 2 }}>Updated: {new Date(data.updatedAt).toLocaleString()}</div>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                    </div>

                    {/* ── Line Items card ───────────────────────────────── */}
                    <div style={{ background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: "hidden", marginBottom: 16 }}>
                        <div style={{ padding: "18px 24px 4px" }}>
                            <SectionHeading icon={FiFileText} title="Line Items" />
                        </div>
                        <div style={{ padding: "0 24px 22px" }}>
                            <LineItemsTable
                                items={edit ? draft.items : (data?.items ?? [])}
                                onChange={(items) => setDraft((prev) => ({ ...prev, items }))}
                                readOnly={!edit}
                                taxRate={taxRate}
                                searchable={searchable}
                                searchSource={searchSource}
                                error={errors.items}
                                itemFieldErrors={itemFieldErrors}
                                totalLabel={totalLabel}
                                forceNegativeTotals={isPCN}
                                docCurrency={draft.currency || "MYR"}
                                currencySettings={currencySettings}
                                {...lineItemProps}
                            />
                        </div>
                    </div>

                    {/* ── Create mode bottom buttons ────────────────────── */}
                    {isAdd && (
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                            <button onClick={cancelEdit} disabled={saving} style={btnStyleOutline}>
                                <FiX size={13} /> Cancel
                            </button>
                            <button
                                onClick={() => save("Draft")}
                                disabled={saving || isInactive}
                                style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#93c5fd" : T.accent, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                            >
                                <FiCheck size={13} /> Save as Draft
                            </button>
                            <button
                                onClick={() => save("Pending for Approval")}
                                disabled={saving || isInactive}
                                style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#a7f3d0" : T.success, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                            >
                                <FiCheck size={13} /> Submit for Approval
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* ─── Discard Modal ─── */}
            <DiscardModal
                open={showDiscard}
                onConfirm={() => {
                    setDiscard(false);
                    if (isAdd) {
                        navigate(listReturnPath(basePath));
                    } else {
                        setDraft({ ...EMPTY, ...data });
                        prevSupplierRef.current = data?.supplier || "";
                        setEdit(false);
                        setErrors({});
                    }
                }}
                onCancel={() => setDiscard(false)}
            />

            <DeleteModal
                target={showDelConfirm ? data : null}
                docType={docType}
                onConfirm={handleDelete}
                onCancel={() => setDelConfirm(false)}
                busy={deleting}
            />

            <CancelDocModal
                target={showCancelConfirm ? data : null}
                docType={docType}
                onConfirm={handleCancelDocument}
                onCancel={() => setCancelConfirm(false)}
                busy={saving}
            />
        </div>
    );
}
